// =========================
// MarketOpportunitySignals — BL-011.3 (Opportunity Intelligence Framework)
//
// Self-contained, flag-gated panel that surfaces Rentava-owned, CROSS-USER
// Opportunity Intelligence for the markets the investor has engaged. Fetches
// from /get-opportunity-signals (derived statistics only, k-anonymity-gated,
// no user identifier) and renders each market's platform signal: how many
// opportunities have been qualified across investors, the Pursue/Investigate/
// Reject mix, and the average qualification score.
//
// ADR-044 / ADR-053 (signal-not-advice): every value is a SIGNAL about
// platform-wide qualification activity — never advice, a forecast, or a
// directive. The panel frames it as "Opportunity Signals" and carries the
// not-advice clarifier. Design tokens only (no hex). The server remains the
// boundary; the entitlement hint here is advisory UX only.
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchOpportunitySignals } from '../../lib/opportunitySignals';
import {
  opportunitySignalsViewed,
  opportunitySignalsEmpty,
} from '../../lib/opportunitySignalsAnalytics';

const SIGNAL_CLARIFIER =
  'Market-wide signals derived from Rentava qualifications — you make the final decision. Not investment advice.';

const sectionTitleStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-lg)',
  fontWeight: 'var(--weight-bold)',
  color: 'var(--color-white)',
  margin: 0,
};

const labelStyle = {
  display: 'block',
  fontSize: 'var(--text-xs)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-wide)',
  color: 'var(--color-amber)',
  marginBottom: 'var(--space-1)',
};

function pct(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${Math.round(rate * 100)}%`;
}

function MarketRow({ m }) {
  const s = m.signal || {};
  return (
    <div
      data-testid="opp-signal-row"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-2) 0',
        borderBottom: '1px solid var(--color-navy-border)',
        gap: 'var(--space-3)',
      }}
    >
      <span style={{ color: 'var(--color-steel-lt)' }}>{m.label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 'var(--weight-bold)', color: 'var(--color-white)' }}>
        {s.sample_size} qualified · Pursue {pct(s.pursue_rate)}
        {s.avg_qualification_score != null ? ` · avg ${s.avg_qualification_score}` : ''}
      </span>
    </div>
  );
}

export default function MarketOpportunitySignals() {
  const [signals, setSignals] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await fetchOpportunitySignals();
    if (loadError) {
      setError(loadError.message || 'Could not load opportunity signals.');
      setSignals([]);
    } else {
      const loaded = Array.isArray(data?.signals) ? data.signals : [];
      setSignals(loaded);
      setEntitlement(data?.entitlement || null);
      // BL-011 Operational Intelligence (item 7) — product telemetry for panel
      // render outcome. PII-safe: counts/flags only (no market label). track()
      // env-tags via BL-025 and no-ops when PostHog is unconfigured.
      opportunitySignalsViewed({ market_count: loaded.length });
      if (loaded.length === 0) opportunitySignalsEmpty();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const header = (
    <header style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <h2 style={sectionTitleStyle}>Opportunity Signals</h2>
      <span style={{ ...labelStyle, marginBottom: 0 }}>Market intelligence</span>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-steel-lt)' }}>{SIGNAL_CLARIFIER}</p>
    </header>
  );

  let body;
  if (loading) {
    body = <p style={{ margin: 0, color: 'var(--color-steel-lt)' }}>Loading opportunity signals…</p>;
  } else if (error) {
    body = (
      <p data-testid="opp-signal-error" role="alert" style={{ margin: 0, color: 'var(--color-danger-lt)' }}>
        {error}
      </p>
    );
  } else if (signals.length === 0) {
    body = (
      <p data-testid="opp-signal-empty" style={{ margin: 0, color: 'var(--color-muted)', fontStyle: 'italic' }}>
        Not enough activity in your markets yet. Market-wide signals appear once enough opportunities are
        qualified across investors.
      </p>
    );
  } else {
    body = signals.map((m) => <MarketRow key={m.market_key} m={m} />);
  }

  return (
    <section
      data-testid="market-opportunity-signals"
      className="card"
      style={{ display: 'grid', gap: 'var(--space-3)', padding: 'var(--space-5)' }}
    >
      {header}
      {body}
      {entitlement && !entitlement.isPro ? (
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-steel)' }}>
          Investor plans unlock the full Deal Analyzer. <Link to="/pricing">See plans</Link>.
        </p>
      ) : null}
    </section>
  );
}
